/**
 * Strip metadata (GPS, camera, dates, names) from pictures before they leave the device —
 * byte-for-byte the same rules as the phone app (apps/mobile/lib/src/media/metadata.dart,
 * security review M6):
 *  - JPEG: drop every APPn segment except JFIF (APP0) and the colour profile (APP2 ICC) and
 *    drop comments; the EXIF orientation is kept as a minimal one-tag EXIF so photos don't
 *    turn sideways.
 *  - PNG: keep only the chunks needed to draw the picture.
 * Anything else (or an unexpected layout) is returned untouched.
 */
export function scrubImageMetadata(bytes: Uint8Array): Uint8Array {
  try {
    if (bytes.length > 3 && bytes[0] === 0xff && bytes[1] === 0xd8) return scrubJpeg(bytes);
    if (bytes.length > 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      return scrubPng(bytes);
    }
  } catch {
    // malformed: fall through and return as-is (the decoder will refuse it too)
  }
  return bytes;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const out = new Uint8Array(total);
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

function startsWith(b: Uint8Array, at: number, s: string): boolean {
  if (at + s.length > b.length) return false;
  for (let k = 0; k < s.length; k++) if (b[at + k] !== s.charCodeAt(k)) return false;
  return true;
}

function scrubJpeg(b: Uint8Array): Uint8Array {
  const parts: Uint8Array[] = [new Uint8Array([0xff, 0xd8])];
  let i = 2;
  let orientation: number | null = null;
  const kept: Uint8Array[] = [];
  while (i + 4 <= b.length) {
    if (b[i] !== 0xff) break;
    const marker = b[i + 1]!;
    if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) {
      i += 2;
      continue;
    }
    if (marker === 0xda) {
      // Start of scan: everything after is image data.
      if (orientation !== null && orientation !== 1) parts.push(exifOrientation(orientation));
      parts.push(...kept, b.subarray(i));
      return concat(parts);
    }
    const len = (b[i + 2]! << 8) | b[i + 3]!;
    if (len < 2 || i + 2 + len > b.length) break;
    const seg = b.subarray(i, i + 2 + len);
    const isApp = marker >= 0xe0 && marker <= 0xef;
    if (marker === 0xe1) {
      orientation ??= readOrientation(b.subarray(i + 4, i + 2 + len));
    } else if (marker === 0xfe) {
      // COM (comment): drop
    } else if (!isApp || marker === 0xe0 || (marker === 0xe2 && startsWith(b, i + 4, "ICC_PROFILE"))) {
      kept.push(seg);
    }
    i += 2 + len;
  }
  return b; // unexpected layout: leave untouched rather than break the picture
}

/** Orientation (tag 0x0112) from an APP1 "Exif\0\0" payload, or null. */
function readOrientation(p: Uint8Array): number | null {
  if (!startsWith(p, 0, "Exif") || p.length < 14) return null;
  const t = p.subarray(6);
  const little = t[0] === 0x49;
  const u16 = (o: number) => (little ? t[o]! | (t[o + 1]! << 8) : (t[o]! << 8) | t[o + 1]!);
  const u32 = (o: number) =>
    (little
      ? t[o]! | (t[o + 1]! << 8) | (t[o + 2]! << 16) | (t[o + 3]! << 24)
      : (t[o]! << 24) | (t[o + 1]! << 16) | (t[o + 2]! << 8) | t[o + 3]!) >>> 0;
  const ifd = u32(4);
  if (ifd + 2 > t.length) return null;
  const n = u16(ifd);
  for (let k = 0; k < n; k++) {
    const e = ifd + 2 + k * 12;
    if (e + 12 > t.length) return null;
    if (u16(e) === 0x0112) {
      const v = u16(e + 8);
      return v >= 1 && v <= 8 ? v : null;
    }
  }
  return null;
}

/** A minimal APP1 EXIF segment carrying only the orientation. */
function exifOrientation(o: number): Uint8Array {
  return new Uint8Array([
    0xff, 0xe1, 0x00, 0x22, // APP1, length 34
    0x45, 0x78, 0x69, 0x66, 0x00, 0x00, // "Exif\0\0"
    0x4d, 0x4d, 0x00, 0x2a, 0x00, 0x00, 0x00, 0x08, // big-endian TIFF, IFD0 at 8
    0x00, 0x01, // one entry
    0x01, 0x12, 0x00, 0x03, 0x00, 0x00, 0x00, 0x01, 0x00, o, 0x00, 0x00, // Orientation SHORT = o
    0x00, 0x00, 0x00, 0x00, // no next IFD
  ]);
}

const PNG_KEEP = new Set(["IHDR", "PLTE", "IDAT", "IEND", "tRNS", "gAMA", "cHRM", "sRGB", "iCCP", "sBIT", "pHYs", "acTL", "fcTL", "fdAT"]);

function scrubPng(b: Uint8Array): Uint8Array {
  const parts: Uint8Array[] = [b.subarray(0, 8)];
  let i = 8;
  while (i + 12 <= b.length) {
    const len = ((b[i]! << 24) | (b[i + 1]! << 16) | (b[i + 2]! << 8) | b[i + 3]!) >>> 0;
    const type = String.fromCharCode(b[i + 4]!, b[i + 5]!, b[i + 6]!, b[i + 7]!);
    const end = i + 12 + len;
    if (end > b.length) return b;
    if (PNG_KEEP.has(type)) parts.push(b.subarray(i, end));
    i = end;
    if (type === "IEND") break;
  }
  return concat(parts);
}
